import { TestId } from "#/test-ids";

import "../../../shared/components/modal.js";
import "../cta.js";

window.Sparkle ??= {};
window.Sparkle.MobileCTA ??= {};

Sparkle.MobileCTA.Modal = class Modal extends Sparkle.MobileCTA.CTA {
  constructor(cta, appendToSelector) {
    super(cta, appendToSelector);
    this.activate = this.activate.bind(this);
    this.trackActivation = this.trackActivation.bind(this);
    this.activationElement = this.activationElement.bind(this);
    this.activateAssessment = this.activateAssessment.bind(this);
    this.overrideActivateBehavior = this.overrideActivateBehavior.bind(this);
    this.directLinkHref = this.directLinkHref.bind(this);
    this.navigateDirectly = this.navigateDirectly.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.createPlaceholder = this.createPlaceholder.bind(this);
    this.setPlaceholderAttribute = this.setPlaceholderAttribute.bind(this);
    this.setContent = this.setContent.bind(this);
    this.unsetContent = this.unsetContent.bind(this);
    this.showPlaceholder = this.showPlaceholder.bind(this);
    this.hidePlaceholder = this.hidePlaceholder.bind(this);
    this.animateModal = this.animateModal.bind(this);
    this.ctaClass = "mobile-cta-modal";
    this.phoneButton;

    const modalOptions = { appendToSelector, class: this.ctaClass, freezeScroll: false };
    if (appendToSelector) {
      modalOptions["appendTo"] = appendToSelector;
    }
    this.modal = new window.Modal(modalOptions);
    this.modal.container.attr("data-testid", TestId.cta.mobileModal);
    const closeButton = this.modal.container.find(".modal-close-button");
    closeButton.attr({
      "aria-label": window.Sparkle.t("frontend.components.modal_close"),
      "data-testid": TestId.cta.mobileModalClose,
      role: "button",
      tabindex: 0,
    });
    closeButton.on("keydown", function (event) {
      if (![13, 32].includes(event.which)) {
        return;
      }

      event.preventDefault();
      event.currentTarget.click();
    });

    // BEHAVIOUR

    this.modal.container.on("beforeShow", this.handleShowModal);
    this.modal.container.on("beforeHide", this.handleHideModal);
  }

  // DSL

  activate() {
    // Emit the analytics "$click" before activating. The activation paths below handle the click
    // programmatically (direct navigation) or open a modal, and never bubble to the global click
    // handler in analytics/index.js, so without this the sticky button tap would go untracked.
    // Tracking first also ensures the event is queued before any navigation unloads the page.
    this.trackActivation();

    if (this.overrideActivateBehavior()) {
      return this.componentBehavior.mobileCTAActivate();
    }

    if (this.activateAssessment()) {
      return; // Assessment was started directly, skipping the intermediary modal
    }

    const directHref = this.directLinkHref();
    if (directHref) {
      return this.navigateDirectly(directHref);
    }

    if (!this.placeholder) {
      this.createPlaceholder();
    }
    this.modal.show();
    return this.modal.backdrop.scrollTop(0); // Added to prevent modal from being scrolled to the bottom when taller than the viewport. Caused by CSS slide-in transition.
  }

  // Track the sticky button tap as a "$click" analytics event, using the underlying actionable
  // element so the event carries the same href/text/record context a direct click on that element
  // would on desktop.
  trackActivation() {
    if (typeof window.Sparkle?.Analytics?.trackElementClick !== "function") {
      return;
    }

    const element = this.activationElement();
    if (element) {
      window.Sparkle.Analytics.trackElementClick(element);
    }
  }

  // The element that best represents the CTA's action so analytics attribution matches the action
  // taken. Uses a priority similar to directLinkHref, additionally considering [data-assessment] and
  // sp-button[href] targets that activate through the modal rather than direct navigation.
  activationElement() {
    const assessment = this.element.find("[data-assessment]");
    if (assessment.length === 1) {
      return assessment.get(0);
    }

    const spInfoCard = this.element.find("sp-info-card[href]");
    if (spInfoCard.length === 1) {
      return spInfoCard.get(0);
    }

    const spButton = this.element.find("sp-button[href]");
    if (spButton.length === 1) {
      return spButton.get(0);
    }

    const links = this.element.find("a[href]");
    if (links.length === 1) {
      return links.get(0);
    }

    return this.element.get(0);
  }

  // When the CTA is an assessment, start it directly instead of showing the intermediary modal with
  // a "Start now" button. Clicking the [data-assessment] element opens the assessment viewer.
  activateAssessment() {
    const assessment = this.element.find("[data-assessment]");
    if (assessment.length !== 1) {
      return false;
    }

    assessment.trigger("click");
    return true;
  }

  overrideActivateBehavior() {
    this.componentBehavior = this.element.data("component-behavior");
    if (this.componentBehavior == null) {
      return;
    }
    if (!this.componentBehavior.mobileCTAActivateOverride) {
      return;
    }
    return this.componentBehavior.mobileCTAActivateOverride();
  }

  directLinkHref() {
    if (this.element.find("button, form, select, .read-more-link, .mini-button, .more-appointments-link").length > 0) {
      return;
    }

    // Check for sp-info-card with a direct href
    const spInfoCard = this.element.find("sp-info-card[href]");
    if (spInfoCard.length === 1) {
      return spInfoCard.attr("href");
    }

    // Check for a single primary CTA button with a direct href (e.g. "Book now").
    // This takes priority over inline body links (such as phone numbers) so that
    // the sticky CTA launches the primary action instead of an inline link.
    const spButton = this.element.find("sp-button[href]");
    if (spButton.length === 1) {
      return spButton.attr("href");
    }

    // Check for a single actionable anchor link
    const links = this.element.find("a[href]");
    if (links.length === 1) {
      return links.attr("href");
    }

    return undefined;
  }

  navigateDirectly(href) {
    if (href.startsWith("#")) {
      const target = document.getElementById(href.substring(1));
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }

    window.location.href = href;
  }

  // EVENT HANDLERS

  handleShowModal(event) {
    this.setContent();
    this.showPlaceholder();
    return this.animateModal("modal-show-animation");
  }

  handleHideModal(event) {
    this.unsetContent();
    this.placeholder.after(this.element);
    this.hidePlaceholder();
    return this.animateModal("modal-hide-animation");
  }

  // HELPERS

  createPlaceholder() {
    let ctaId;
    if (this.elementId) {
      ctaId = [this.ctaClass, this.elementId].join("_");
    }
    if (jQuery(`#${ctaId}`).first().length) {
      return (this.placeholder = jQuery(`#${ctaId}`).first());
    } else {
      this.placeholder = this.element.clone(true);
      this.placeholder = this.setPlaceholderAttribute(this.placeholder, "id");
      this.placeholder = this.setPlaceholderAttribute(this.placeholder, "for");
      this.hidePlaceholder();
      return this.element.after(this.placeholder);
    }
  }

  setPlaceholderAttribute(placeholder, attr) {
    if (placeholder.attr(attr)) {
      placeholder.attr(attr, [this.ctaClass, jQuery(placeholder).attr(attr)].join("_"));
    }
    placeholder.find(`[${attr}]`).each(function (index, element) {
      if (jQuery(element).attr(attr)) {
        return jQuery(element).attr(attr, [this.ctaClass, jQuery(element).attr(attr)].join("_"));
      }
    });

    return placeholder;
  }

  setContent() {
    const content = this.formattedContent();
    this.modal.header.css("color", content.css("color"));
    return this.modal.setBody(content);
  }

  unsetContent() {
    if (this.phoneButton) {
      return this.element.find(this.phoneButton).remove();
    }
  }

  showPlaceholder() {
    return this.placeholder.show();
  }

  hidePlaceholder() {
    return this.placeholder.hide();
  }

  animateModal(animationClass) {
    const animationContainer = this.modal.container.addClass(animationClass);

    return animationContainer.one("webkitAnimationEnd mozAnimationEnd oAnimationEnd oanimationend animationend", () =>
      animationContainer.removeClass(animationClass),
    );
  }

  formattedContent() {
    this.addPhoneNumberButton(this.element);
    return this.element;
  }

  addPhoneNumberButton() {
    if (this.element.find("button, form, .read-more-link, .mini-button, .more-appointments-link").length !== 0) {
      return;
    } // Do not create if a button already exists
    if (this.element.find("select").length !== 0) {
      return;
    } // Do not create if there is a select and the content can change

    const phone = this.element.find(".telephone-link");
    if (phone.length !== 1) {
      return;
    } // Do not create if there are multiple phone numbers (or if there are none).

    this.phoneButton = jQuery("<sp-button>", {
      tag: "a",
      href: phone.attr("href"),
      label: "Call now",
      color: "primary",
      size: "default",
    });

    this.element.append(this.phoneButton);

    return this.element;
  }
};
